// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

import './styles/main.css';
import { EventLog, stamp, type LogEntry } from './eventlog.ts';
import { extract } from './extract.ts';
import { mountGlossary } from './glossary.ts';
import { auditCsv, packageJson, reportHtml, requirementsTxt, unresolvedTxt } from './exports.ts';
import { buildFinding, DEFAULT_THRESHOLDS, editBudget, headline, type Thresholds } from './score.ts';
import { verifyAll } from './verify.ts';
import type { Candidate, Excluded, Finding, Neighbour, RegistryFacts } from './types.ts';
import type { Incoming, ReadyMessage, ResultMessage } from './worker.ts';

const SETTINGS_KEY = 'ghostdep:settings:v1';

/* ── state ────────────────────────────────────────────────────────────────── */

interface State {
  text: string;
  filename: string | null;
  candidates: readonly Candidate[];
  excluded: readonly Excluded[];
  notes: readonly string[];
  facts: Map<string, RegistryFacts>;
  neighbours: Map<string, readonly Neighbour[]>;
  ownPopularity: Map<string, number | null>;
  thresholds: Thresholds;
  phase: 'idle' | 'extracted' | 'checking' | 'done';
  progress: { done: number; total: number; label: string };
  error: string | null;
}

const state: State = {
  text: '',
  filename: null,
  candidates: [],
  excluded: [],
  notes: [],
  facts: new Map(),
  neighbours: new Map(),
  ownPopularity: new Map(),
  thresholds: loadThresholds(),
  phase: 'idle',
  progress: { done: 0, total: 0, label: '' },
  error: null,
};

function loadThresholds(): Thresholds {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_THRESHOLDS;
    const parsed = JSON.parse(raw) as Partial<Thresholds>;
    return {
      lengthPerEdit: clamp(Number(parsed.lengthPerEdit) || DEFAULT_THRESHOLDS.lengthPerEdit, 3, 14),
      popularityRatio: clamp(Number(parsed.popularityRatio) || DEFAULT_THRESHOLDS.popularityRatio, 2, 100_000),
      newDays: DEFAULT_THRESHOLDS.newDays,
      unpopularWeekly: DEFAULT_THRESHOLDS.unpopularWeekly,
    };
  } catch {
    return DEFAULT_THRESHOLDS;
  }
}

function saveThresholds(): void {
  try {
    localStorage.setItem(
      SETTINGS_KEY,
      JSON.stringify({ lengthPerEdit: state.thresholds.lengthPerEdit, popularityRatio: state.thresholds.popularityRatio }),
    );
  } catch {
    /* private browsing — the sliders still work, they just do not persist */
  }
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

const log = new EventLog();
const app = document.getElementById('app') as HTMLElement;

const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
let queryId = 0;
const pending = new Map<number, (r: { neighbours: readonly Neighbour[]; ownPopularity: number | null }) => void>();
let controller: AbortController | null = null;

worker.addEventListener('message', (event: MessageEvent<ResultMessage | ReadyMessage>) => {
  const message = event.data;
  if (message.t === 'ready') {
    log.add(`Name tables ready — ${message.npm.toLocaleString()} npm and ${message.pypi.toLocaleString()} PyPI names, indexed in ${message.ms} ms`, 'good');
    return;
  }
  const resolve = pending.get(message.id);
  if (resolve) {
    pending.delete(message.id);
    resolve({ neighbours: message.neighbours, ownPopularity: message.ownPopularity });
  }
});

function askNeighbours(candidate: Candidate): Promise<{ neighbours: readonly Neighbour[]; ownPopularity: number | null }> {
  return new Promise((resolve) => {
    const id = ++queryId;
    pending.set(id, resolve);
    // Ask generously — up to three edits — and let the thresholds filter. The
    // sliders can then widen without a round trip.
    const message: Incoming = { t: 'query', id, ecosystem: candidate.ecosystem, name: candidate.name, maxCost: 6 };
    worker.postMessage(message);
  });
}

/* ── small DOM helpers ────────────────────────────────────────────────────── */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function term(word: string, key: string): HTMLElement {
  const span = el('span', 'glossary-link', word);
  span.dataset.term = key;
  span.tabIndex = 0;
  return span;
}

function download(content: string, filename: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = el('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  log.add(`Saved ${filename}`, 'good');
}

/* ── ingest ───────────────────────────────────────────────────────────────── */

function ingestText(text: string, filename: string | null, origin: string): void {
  state.text = text;
  state.filename = filename;
  state.facts = new Map();
  state.neighbours = new Map();
  state.ownPopularity = new Map();
  state.error = null;

  const result = extract(text, filename ?? undefined);
  state.candidates = result.candidates;
  state.excluded = result.excluded;
  state.notes = result.notes;
  state.phase = result.candidates.length ? 'extracted' : 'idle';

  log.add(
    `Read ${text.length.toLocaleString()} characters from ${origin} — found ${result.candidates.length} package name${result.candidates.length === 1 ? '' : 's'}, ignored ${result.excluded.length}`,
    result.candidates.length ? 'good' : 'warn',
  );
  for (const note of result.notes) log.add(note);
  render();
  if (result.candidates.length) void run();
}

async function ingestFile(file: File): Promise<void> {
  const text = await file.text();
  ingestText(text, file.name, file.name);
}

async function loadSample(): Promise<void> {
  try {
    const response = await fetch('samples/ai-answer.md');
    if (!response.ok) throw new Error(`the sample returned ${response.status}`);
    const text = await response.text();
    // Deliberately through the same path as a real paste — a sample that took
    // a shortcut would demonstrate a code path no user ever runs.
    ingestText(text, 'ai-answer.md', 'the sample answer');
  } catch (error) {
    state.error = `Could not load the sample: ${error instanceof Error ? error.message : 'unknown error'}`;
    log.add(state.error, 'bad');
    render();
  }
}

/* ── the run ──────────────────────────────────────────────────────────────── */

async function run(): Promise<void> {
  controller?.abort();
  controller = new AbortController();
  const signal = controller.signal;

  state.phase = 'checking';
  state.progress = { done: 0, total: state.candidates.length, label: '' };
  state.error = null;
  render();

  // Neighbours first: they are local, instant, and they mean the table has
  // something useful in it even if every network request fails.
  await Promise.all(
    state.candidates.map(async (candidate) => {
      const answer = await askNeighbours(candidate);
      state.neighbours.set(`${candidate.ecosystem}:${candidate.name}`, answer.neighbours);
      state.ownPopularity.set(`${candidate.ecosystem}:${candidate.name}`, answer.ownPopularity);
    }),
  );
  render();

  log.add(`Looking up ${state.candidates.length} name${state.candidates.length === 1 ? '' : 's'} — only the names are sent, never your text`, 'net');

  try {
    const facts = await verifyAll(
      { fetch: (input, init) => fetch(input, init), signal },
      state.candidates.map((c) => ({ ecosystem: c.ecosystem, name: c.name })),
      (done, total, name) => {
        if (signal.aborted) return;
        state.progress = { done, total, label: name };
        log.add(`${name} — ${done}/${total}`, 'net');
        renderProgress();
      },
    );
    if (signal.aborted) return;
    state.facts = facts;
    state.phase = 'done';
    const summary = headline(findings());
    log.add(summary, findings().some((f) => f.verdict === 'not-in-registry') ? 'bad' : 'good');
  } catch (error) {
    if (signal.aborted) return;
    state.error = error instanceof Error ? error.message : 'the lookup failed';
    state.phase = 'done';
    log.add(`Lookup failed: ${state.error}`, 'bad');
  }
  render();
}

function findings(): Finding[] {
  const now = Date.now();
  return state.candidates.map((candidate) => {
    const key = `${candidate.ecosystem}:${candidate.name}`;
    const facts = state.facts.get(key) ?? {
      existence: 'unchecked' as const,
      firstPublished: null,
      latestVersion: null,
      weeklyDownloads: null,
      repository: null,
      releaseCount: null,
      hasInstallScript: null,
      maintainerCount: null,
      yanked: null,
      integrity: null,
      sha256: [],
      error: null,
    };
    return buildFinding(candidate, facts, state.neighbours.get(key) ?? [], state.thresholds, now, state.ownPopularity.get(key) ?? null);
  });
}

/* ── render ───────────────────────────────────────────────────────────────── */

function render(): void {
  app.replaceChildren(topbar(), mainContent(), footer());
}

function topbar(): HTMLElement {
  const bar = el('header', 'topbar');
  const brand = el('div', 'brand');
  brand.append(el('span', 'brand-mark', 'ghostdep'));
  brand.append(el('span', 'brand-tag', 'does this package actually exist?'));
  bar.append(brand);

  const nav = el('nav', 'topnav');
  const add = (label: string, onClick: () => void) => {
    const button = el('button', 'btn', label);
    button.addEventListener('click', onClick);
    nav.append(button);
  };
  add('How it works', () => openModal('How ghostdep works', 'tpl-how'));
  add('Privacy', () => openModal('Privacy & threat model', 'tpl-privacy'));
  add('About', () => openModal('About ghostdep', 'tpl-about'));
  add('Event log', () => openLog());
  bar.append(nav);
  return bar;
}

function footer(): HTMLElement {
  const foot = el('footer', 'site-footer');
  const inner = el('div', 'footer-inner');
  const built = el('span');
  built.append(document.createTextNode('Built by '));
  const a1 = el('a');
  a1.href = 'https://benrichardson.dev/';
  a1.target = '_blank';
  a1.rel = 'noopener';
  a1.textContent = 'benrichardson.dev';
  const a2 = el('a');
  a2.href = 'https://lab.benrichardson.dev';
  a2.target = '_blank';
  a2.rel = 'noopener';
  a2.textContent = 'more tools & sites';
  built.append(a1, document.createTextNode(' · '), a2);
  inner.append(built);
  foot.append(inner);
  return foot;
}

function mainContent(): HTMLElement {
  const main = el('main', 'main-content');
  main.append(trustBanner());
  main.append(inputSection());
  if (state.error) {
    const error = el('div', 'error-box');
    error.append(el('strong', undefined, 'Something went wrong. '), document.createTextNode(state.error));
    const retry = el('button', 'btn btn-primary', 'Try again');
    retry.addEventListener('click', () => void run());
    error.append(retry);
    main.append(error);
  }
  if (state.phase === 'checking') main.append(progressSection());
  if (state.candidates.length) {
    main.append(resultsSection());
    main.append(calibrationSection());
    main.append(exportsSection());
  }
  if (state.excluded.length) main.append(excludedSection());
  return main;
}

function trustBanner(): HTMLElement {
  const banner = el('button', 'trust-banner');
  banner.type = 'button';
  banner.append(
    el('strong', undefined, 'Your code never leaves this tab.'),
    document.createTextNode(
      ' Only the package names themselves are looked up, because only the registry can say whether a name exists. Nothing else is sent anywhere.',
    ),
  );
  banner.addEventListener('click', () => openModal('Privacy & threat model', 'tpl-privacy'));
  return banner;
}

function inputSection(): HTMLElement {
  const section = el('section', 'input-section');

  const zone = el('div', 'dropzone');
  zone.tabIndex = 0;
  zone.setAttribute('role', 'button');
  zone.setAttribute('aria-label', 'Paste an answer, or drop a file');

  const heading = el('div', 'dz-title', 'Paste the answer your assistant gave you');
  const sub = el('div', 'dz-sub');
  sub.append(
    document.createTextNode('Or drop a '),
    el('code', undefined, 'requirements.txt'),
    document.createTextNode(', a '),
    el('code', undefined, 'package.json'),
    document.createTextNode(', or a source file. Imports and install commands are both read.'),
  );

  const textarea = el('textarea', 'paste-area');
  textarea.placeholder =
    'pip install requests beautifulsoup4 pandas\n\nimport cv2\nfrom sklearn.model_selection import train_test_split\n\nnpm install react axios date-fns';
  textarea.value = state.text;
  textarea.setAttribute('aria-label', 'Paste your assistant’s answer here');
  textarea.addEventListener('change', () => {
    if (textarea.value.trim()) ingestText(textarea.value, null, 'the paste box');
  });

  const actions = el('div', 'dz-actions');
  const check = el('button', 'btn btn-primary', 'Check these packages');
  check.addEventListener('click', () => {
    if (textarea.value.trim()) ingestText(textarea.value, null, 'the paste box');
  });

  const picker = el('input');
  picker.type = 'file';
  picker.accept = '.txt,.json,.md,.py,.js,.ts,.jsx,.tsx,.mjs,.cjs,.cfg,.in,text/*';
  picker.hidden = true;
  picker.addEventListener('change', () => {
    const file = picker.files?.[0];
    if (file) void ingestFile(file);
  });
  const choose = el('button', 'btn', 'Choose a file');
  choose.addEventListener('click', () => picker.click());

  const sample = el('button', 'btn btn-ghost', 'Try a sample');
  sample.addEventListener('click', (event) => {
    // Inside the drop zone, so the click must not also open the file picker.
    event.stopPropagation();
    void loadSample();
  });

  actions.append(check, choose, sample, picker);
  zone.append(heading, sub, textarea, actions);

  zone.addEventListener('dragover', (event) => {
    event.preventDefault();
    zone.classList.add('is-over');
  });
  zone.addEventListener('dragleave', () => zone.classList.remove('is-over'));
  zone.addEventListener('drop', (event) => {
    event.preventDefault();
    zone.classList.remove('is-over');
    const file = event.dataTransfer?.files?.[0];
    if (file) void ingestFile(file);
  });

  section.append(zone);
  for (const note of state.notes) section.append(el('p', 'note', note));
  return section;
}

let progressHost: HTMLElement | null = null;

function progressSection(): HTMLElement {
  const section = el('section', 'progress-section');
  progressHost = section;
  paintProgress(section);
  return section;
}

function renderProgress(): void {
  if (progressHost) paintProgress(progressHost);
}

function paintProgress(host: HTMLElement): void {
  const { done, total, label } = state.progress;
  const pct = total ? Math.round((done / total) * 100) : 0;
  host.replaceChildren();
  const bar = el('div', 'bar');
  const fill = el('div', 'bar-fill');
  fill.style.width = `${pct}%`;
  bar.append(fill);
  const caption = el('div', 'progress-caption', label ? `Checking ${label} — ${done} of ${total}` : `Checking ${total} names`);
  caption.setAttribute('aria-live', 'polite');
  host.append(bar, caption);
}

const VERDICT_LABEL: Record<string, string> = {
  'not-in-registry': 'Not in the registry',
  'very-new': 'Newly published',
  'near-popular-name': 'Close to a bigger name',
  unpopular: 'Little used',
  established: 'Established',
  'not-checked': 'Not checked',
};

const VERDICT_ORDER: Record<string, number> = {
  'not-in-registry': 0,
  'very-new': 1,
  'near-popular-name': 2,
  'not-checked': 3,
  unpopular: 4,
  established: 5,
};

function resultsSection(): HTMLElement {
  const section = el('section', 'results');
  const rows = findings();
  const summary = el('h2', 'headline', headline(rows));
  section.append(summary);

  const sorted = [...rows].sort(
    (a, b) => (VERDICT_ORDER[a.verdict] ?? 9) - (VERDICT_ORDER[b.verdict] ?? 9) || a.candidate.name.localeCompare(b.candidate.name),
  );

  const wrap = el('div', 'table-wrap');
  const table = el('table', 'results-table');
  const thead = el('thead');
  const headRow = el('tr');
  for (const label of ['Package', 'Where', 'Verdict', 'Latest', 'Age', 'Weekly', 'Nearest popular name', '']) {
    headRow.append(el('th', undefined, label));
  }
  thead.append(headRow);
  table.append(thead);

  const tbody = el('tbody');
  for (const finding of sorted) tbody.append(resultRow(finding));
  table.append(tbody);
  wrap.append(table);
  section.append(wrap);
  return section;
}

function resultRow(finding: Finding): HTMLElement {
  const row = el('tr', `row v-${finding.verdict}`);

  const nameCell = el('td', 'cell-name');
  nameCell.append(el('code', undefined, finding.candidate.name));
  if (finding.candidate.mappingNote) {
    const mapped = el('span', 'mapping', ` ← ${finding.candidate.raw}`);
    mapped.title = finding.candidate.mappingNote;
    nameCell.append(mapped);
  }
  if (finding.facts.hasInstallScript) {
    const flag = el('span', 'chip chip-neutral');
    flag.append(term('install script', 'install-script'));
    nameCell.append(document.createTextNode(' '), flag);
  }
  if (finding.facts.yanked) nameCell.append(document.createTextNode(' '), el('span', 'chip chip-neutral', 'has yanked files'));
  row.append(nameCell);

  row.append(el('td', 'cell-dim', `${finding.candidate.ecosystem} · ${finding.candidate.origin}`));

  const verdictCell = el('td');
  verdictCell.append(el('span', `pill pill-${finding.verdict}`, VERDICT_LABEL[finding.verdict] ?? finding.verdict));
  row.append(verdictCell);

  row.append(el('td', 'cell-mono', finding.facts.latestVersion ?? '—'));

  const age = finding.facts.firstPublished ? new Date(finding.facts.firstPublished) : null;
  const ageCell = el('td', 'cell-mono');
  if (age) {
    const days = Math.floor((Date.now() - age.getTime()) / 86_400_000);
    ageCell.textContent = days < 365 ? `${days}d` : `${Math.floor(days / 365)}y`;
    ageCell.title = `First published ${age.toISOString().slice(0, 10)}`;
  } else if (finding.facts.existence === 'exists' && finding.candidate.ecosystem === 'npm') {
    ageCell.textContent = '—';
    ageCell.title =
      'Not looked up. npm only reports a creation date in the full package document, which is megabytes for a popular package, so ghostdep fetches it only for packages below the popularity threshold — where the age is the question.';
  } else {
    ageCell.textContent = '—';
  }
  row.append(ageCell);

  const weekly = el('td', 'cell-mono');
  if (finding.facts.weeklyDownloads !== null) {
    weekly.textContent = compact(finding.facts.weeklyDownloads);
    weekly.title = `${finding.facts.weeklyDownloads.toLocaleString()} downloads in the last week`;
  } else if (finding.candidate.ecosystem === 'pypi') {
    weekly.textContent = 'n/a';
    weekly.title =
      'PyPI download counts come from pypistats.org, which sends no access-control-allow-origin header, so a browser cannot read them. ghostdep would rather say nothing than substitute a number from somewhere else.';
  } else {
    weekly.textContent = '—';
  }
  row.append(weekly);

  const nearCell = el('td', 'cell-near');
  if (finding.neighbours.length) {
    const near = finding.neighbours[0];
    nearCell.append(el('code', undefined, near.name));
    nearCell.append(el('span', 'muted', ` ${(near.cost / 2).toString()} edit${near.cost === 2 ? '' : 's'}`));
  } else {
    nearCell.textContent = '—';
  }
  row.append(nearCell);

  const why = el('td', 'cell-why');
  why.append(el('span', 'reason', finding.reason));
  if (finding.facts.repository) {
    const link = el('a', 'repo-link', 'repo');
    link.href = finding.facts.repository;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    why.append(document.createTextNode(' '), link);
  }
  row.append(why);
  return row;
}

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

function calibrationSection(): HTMLElement {
  const section = el('section', 'calibration');
  const head = el('div', 'cal-head');
  head.append(el('h3', undefined, 'Calibrate what counts as “too close”'));
  const blurb = el('p', 'note');
  blurb.append(
    document.createTextNode('No default is right for everyone, so both thresholds are yours. Watch the matches below change as you drag: '),
    term('ms, qs and ws', 'budget'),
    document.createTextNode(' are three real, hugely popular packages one edit from each other, and a tool that warns about them is a tool you stop reading.'),
  );
  head.append(blurb);
  section.append(head);

  const grid = el('div', 'cal-grid');

  const lengthRow = el('div', 'cal-row');
  const lengthLabel = el('label', 'cal-label');
  lengthLabel.append(term('Edit budget', 'edit-distance'));
  lengthLabel.append(document.createTextNode(`: one edit per ${state.thresholds.lengthPerEdit} characters`));
  const lengthInput = el('input', 'slider');
  lengthInput.type = 'range';
  lengthInput.min = '3';
  lengthInput.max = '14';
  lengthInput.step = '1';
  lengthInput.value = String(state.thresholds.lengthPerEdit);
  lengthInput.setAttribute('aria-label', 'Characters of name length required per permitted edit');
  lengthInput.addEventListener('input', () => {
    state.thresholds = { ...state.thresholds, lengthPerEdit: Number(lengthInput.value) };
    saveThresholds();
    render();
  });
  lengthRow.append(lengthLabel, lengthInput);

  const ratioRow = el('div', 'cal-row');
  const ratioLabel = el('label', 'cal-label');
  ratioLabel.append(term('Popularity ratio', 'ratio'));
  ratioLabel.append(document.createTextNode(`: the neighbour must be ${state.thresholds.popularityRatio.toLocaleString()}× bigger`));
  const ratioInput = el('input', 'slider');
  ratioInput.type = 'range';
  ratioInput.min = '0';
  ratioInput.max = '40';
  ratioInput.step = '1';
  // Log scale: the interesting range spans 2x to 100,000x and a linear slider
  // would spend nine tenths of its travel above 10,000.
  ratioInput.value = String(Math.round(Math.log10(state.thresholds.popularityRatio / 2) * 10));
  ratioInput.setAttribute('aria-label', 'How many times more popular a neighbour must be before it is mentioned');
  ratioInput.addEventListener('input', () => {
    const ratio = Math.round(2 * 10 ** (Number(ratioInput.value) / 10));
    state.thresholds = { ...state.thresholds, popularityRatio: ratio };
    saveThresholds();
    render();
  });
  ratioRow.append(ratioLabel, ratioInput);

  grid.append(lengthRow, ratioRow);
  section.append(grid);

  const matches = findings().filter((f) => f.neighbours.length);
  const list = el('div', 'cal-matches');
  if (!matches.length) {
    list.append(el('p', 'note', 'At these settings, no package in your list is close enough to a much bigger name to be worth mentioning.'));
  } else {
    for (const finding of matches) {
      const line = el('div', 'cal-match');
      line.append(el('code', undefined, finding.candidate.name));
      line.append(el('span', 'muted', ' → '));
      line.append(el('code', undefined, finding.neighbours[0].name));
      line.append(
        el(
          'span',
          'muted',
          `  ${(finding.neighbours[0].cost / 2).toString()} edits, budget ${(editBudget(finding.candidate.name, state.thresholds.lengthPerEdit) / 2).toString()}`,
        ),
      );
      list.append(line);
    }
  }
  section.append(list);
  return section;
}

function exportsSection(): HTMLElement {
  const section = el('section', 'exports');
  section.append(el('h3', undefined, 'Take it with you'));
  const note = el('p', 'note');
  note.append(
    document.createTextNode('One file per click, on purpose: Safari silently suppresses every download after the first in a single gesture. The requirements file explains its own '),
    term('--require-hashes', 'require-hashes'),
    document.createTextNode(' limitation inside the file.'),
  );
  section.append(note);

  const rows = findings();
  const now = Date.now();
  const buttons = el('div', 'export-buttons');
  const add = (label: string, make: () => { body: string; name: string; type: string }, enabled: boolean) => {
    const button = el('button', 'btn', label);
    button.disabled = !enabled;
    button.addEventListener('click', () => {
      const { body, name, type } = make();
      download(body, name, type);
    });
    buttons.append(button);
  };

  const hasPypi = rows.some((f) => f.candidate.ecosystem === 'pypi');
  const hasNpm = rows.some((f) => f.candidate.ecosystem === 'npm' && f.facts.existence === 'exists');
  const hasMissing = rows.some((f) => f.verdict === 'not-in-registry' || f.verdict === 'not-checked');

  add('requirements.txt', () => ({ body: requirementsTxt(rows), name: 'requirements.txt', type: 'text/plain' }), hasPypi);
  add('package.json', () => ({ body: packageJson(rows), name: 'package.json', type: 'application/json' }), hasNpm);
  add('unresolved.txt', () => ({ body: unresolvedTxt(rows), name: 'unresolved.txt', type: 'text/plain' }), hasMissing);
  add('dependency-audit.csv', () => ({ body: auditCsv(rows, now), name: 'dependency-audit.csv', type: 'text/csv' }), rows.length > 0);
  add(
    'report.html',
    () => ({ body: reportHtml(rows, headline(rows), state.thresholds, now), name: 'ghostdep-report.html', type: 'text/html' }),
    rows.length > 0,
  );

  const copy = el('button', 'btn', 'Copy the summary');
  copy.addEventListener('click', () => {
    void navigator.clipboard?.writeText(`${headline(rows)}\n\n${unresolvedTxt(rows)}`);
    log.add('Copied the summary to the clipboard', 'good');
  });
  buttons.append(copy);

  if (typeof navigator.share === 'function') {
    const share = el('button', 'btn', 'Share');
    share.addEventListener('click', () => {
      void navigator.share({ title: 'ghostdep audit', text: headline(rows) }).catch(() => {
        /* the user dismissed the sheet */
      });
    });
    buttons.append(share);
  }

  section.append(buttons);
  return section;
}

function excludedSection(): HTMLElement {
  const section = el('section', 'excluded');
  const details = el('details');
  const summary = el('summary', undefined, `${state.excluded.length} name${state.excluded.length === 1 ? '' : 's'} deliberately not checked`);
  details.append(summary);
  const note = el('p', 'note', 'Shown because a tool that quietly drops half its input looks exactly like one that found nothing wrong.');
  details.append(note);
  const list = el('ul', 'excluded-list');
  for (const item of state.excluded) {
    const li = el('li');
    li.append(el('code', undefined, item.raw), el('span', 'muted', ` — ${item.reason}`));
    list.append(li);
  }
  details.append(list);
  section.append(details);
  return section;
}

/* ── overlays ─────────────────────────────────────────────────────────────── */

/**
 * Every overlay gets all four exits: the × control, a tap anywhere outside the
 * panel, Escape, and a real touch sequence on the backdrop. They are not
 * alternatives. An overlay you cannot dismiss makes the whole tool unusable,
 * and on a phone the control that opened it is underneath the overlay.
 *
 * `pointerdown` rather than `click`, because it covers mouse, touch and pen in
 * one handler and fires where a synthetic click does not — and because
 * attaching both `click` and `touchstart` to one control double-fires on a real
 * tap, closing and instantly reopening, which reads as a dead button.
 */
function mountOverlay(node: HTMLElement, panel: HTMLElement, onClose: () => void): () => void {
  const close = () => {
    cleanup();
    onClose();
  };
  const onPointer = (event: PointerEvent) => {
    if (!panel.contains(event.target as Node)) close();
  };
  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  };
  const cleanup = () => {
    node.removeEventListener('pointerdown', onPointer);
    document.removeEventListener('keydown', onKey, true);
    node.remove();
  };
  node.addEventListener('pointerdown', onPointer);
  document.addEventListener('keydown', onKey, true);
  return close;
}

function openModal(title: string, templateId: string): void {
  const template = document.getElementById(templateId) as HTMLTemplateElement | null;
  const backdrop = el('div', 'modal-backdrop');
  const modal = el('div', 'modal');
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', title);

  const head = el('div', 'modal-head');
  head.append(el('h2', 'modal-title', title));
  const x = el('button', 'close-x', '✕');
  x.setAttribute('aria-label', 'Close');
  head.append(x);

  const body = el('div', 'modal-body');
  if (template) body.append(template.content.cloneNode(true));

  modal.append(head, body);
  backdrop.append(modal);
  document.body.append(backdrop);

  const close = mountOverlay(backdrop, modal, () => {});
  x.addEventListener('pointerdown', (event) => {
    event.stopPropagation();
    close();
  });
  x.focus();
}

function openLog(): void {
  const backdrop = el('div', 'log-backdrop');
  const drawer = el('aside', 'log-drawer');
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-label', 'Event log');

  const head = el('div', 'log-head');
  head.append(el('strong', undefined, 'Event log'));
  const copy = el('button', 'btn', 'Copy');
  copy.addEventListener('click', () => void navigator.clipboard?.writeText(log.toText()));
  const x = el('button', 'close-x', '✕');
  x.setAttribute('aria-label', 'Close the event log');
  head.append(copy, x);

  const list = el('div', 'log-list');
  const paint = (entries: readonly LogEntry[]) => {
    list.replaceChildren();
    for (const entry of entries) {
      const line = el('div', `log-line is-${entry.kind}`);
      line.append(el('span', 'log-time', `[${stamp(entry.at)}] `), document.createTextNode(entry.message));
      list.append(line);
    }
    list.scrollTop = list.scrollHeight;
  };
  const unsubscribe = log.subscribe(paint);

  drawer.append(head, list);
  backdrop.append(drawer);
  document.body.append(backdrop);

  const close = mountOverlay(backdrop, drawer, unsubscribe);
  x.addEventListener('pointerdown', (event) => {
    event.stopPropagation();
    close();
  });
}

/* ── boot ─────────────────────────────────────────────────────────────────── */

mountGlossary();
render();
worker.postMessage({ t: 'warm' } satisfies Incoming);
log.add('ghostdep ready — nothing has been uploaded and nothing will be');

document.addEventListener('keydown', (event) => {
  if (event.key === '?' && !(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLTextAreaElement)) {
    openModal('How ghostdep works', 'tpl-how');
  }
});

window.addEventListener('paste', (event) => {
  if (document.activeElement instanceof HTMLTextAreaElement || document.activeElement instanceof HTMLInputElement) return;
  const text = event.clipboardData?.getData('text/plain');
  if (!text || text.trim().length < 8) return;
  event.preventDefault();
  ingestText(text, null, 'the clipboard');
});
